import { useCallback, useMemo, useRef, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Download,
  GripVertical,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
  File as FileIcon,
  FileImage,
  FileText,
  FileSpreadsheet,
  FileArchive,
  FileAudio,
  FileVideo,
} from 'lucide-react';
import { notify } from '@/lib/notify';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { uploadFile } from '@/lib/upload';
import { cn } from '@/lib/utils';

interface AttachmentDto {
  id: number;
  fileId: number;
  name: string;
  mimeType: string;
  size: number;
  url: string;
  order: number;
  createdAt: string;
}

interface ModuleItemAttachmentsProps {
  moduleItemId: string | number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  errors?: string[] | null;
}

function pickIcon(mime: string): React.ComponentType<{ className?: string }> {
  if (mime.startsWith('image/')) {return FileImage;}
  if (mime.startsWith('video/')) {return FileVideo;}
  if (mime.startsWith('audio/')) {return FileAudio;}
  if (mime === 'application/pdf') {return FileText;}
  if (
    mime === 'application/msword' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return FileText;
  }
  if (
    mime === 'application/vnd.ms-excel' ||
    mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mime === 'text/csv'
  ) {
    return FileSpreadsheet;
  }
  if (
    mime === 'application/zip' ||
    mime === 'application/x-rar-compressed' ||
    mime === 'application/x-7z-compressed'
  ) {
    return FileArchive;
  }
  if (mime.startsWith('text/')) {return FileText;}
  return FileIcon;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) {return `${bytes} B`;}
  if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
  if (bytes < 1024 * 1024 * 1024) {return `${(bytes / 1024 / 1024).toFixed(1)} MB`;}
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return token !== null && token.length > 0
    ? { Authorization: `Bearer ${token}` }
    : {};
}

// ─── Sortable row ─────────────────────────────────────────────

interface SortableAttachmentProps {
  attachment: AttachmentDto;
  onDelete: (id: number) => void;
  isDeleting: boolean;
}

function SortableAttachment({
  attachment,
  onDelete,
  isDeleting,
}: SortableAttachmentProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: attachment.id });
  const Icon = pickIcon(attachment.mimeType);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2',
        isDragging && 'opacity-50 z-50',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="shrink-0 cursor-grab p-1 text-muted-foreground hover:text-foreground active:cursor-grabbing"
        aria-label="Перетащить"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Icon className="h-5 w-5 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{attachment.name}</p>
        <p className="text-xs text-muted-foreground">
          {formatSize(attachment.size)}
        </p>
      </div>
      <a
        href={attachment.url}
        download={attachment.name}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 rounded p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
        title="Скачать"
      >
        <Download className="h-4 w-4" />
      </a>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0"
        disabled={isDeleting}
        onClick={() => onDelete(attachment.id)}
        title="Удалить"
      >
        {isDeleting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Trash2 className="h-4 w-4 text-destructive" />
        )}
      </Button>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────

export function ModuleItemAttachments({ moduleItemId }: ModuleItemAttachmentsProps) {
  const apiUrl = useApiUrl();
  const baseUrl = useMemo(
    () => `${apiUrl}/module-items/${moduleItemId}/attachments`,
    [apiUrl, moduleItemId],
  );

  const [attachments, setAttachments] = useState<AttachmentDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Load attachments — запускается при первом рендере, без react-query
  // чтобы не вводить новую зависимость. При смене moduleItemId перезагружается.
  const reload = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(baseUrl, { headers: getAuthHeaders() });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = (await res.json()) as ApiResponse<AttachmentDto[]>;
      if (!json.success) {
        throw new Error(json.errors?.[0] ?? 'Ошибка загрузки списка');
      }
      setAttachments(json.data);
    } catch (err: unknown) {
      notify.error(
        err instanceof Error ? err.message : 'Не удалось загрузить вложения',
      );
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl]);

  if (!initializedRef.current) {
    initializedRef.current = true;
    void reload();
  }

  const handleUpload = useCallback(
    async (file: File) => {
      setUploading(true);
      setUploadProgress(0);
      try {
        // Шаг 1: загрузка файла в общий upload-эндпоинт
        const uploaded = await uploadFile({
          file,
          access: 'PRIVATE',
          onProgress: ({ percent }) => setUploadProgress(percent),
        });

        // Шаг 2: привязка файла к уроку
        const attachRes = await fetch(baseUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ fileId: uploaded.id }),
        });

        if (!attachRes.ok) {
          const errJson = (await attachRes.json().catch(() => null)) as
            | ApiResponse<unknown>
            | null;
          throw new Error(
            errJson?.errors?.[0] ??
              `Не удалось прикрепить файл (HTTP ${attachRes.status})`,
          );
        }

        const result = (await attachRes.json()) as ApiResponse<AttachmentDto>;
        if (!result.success) {
          throw new Error(result.errors?.[0] ?? 'Ошибка привязки');
        }

        setAttachments((prev) => [...prev, result.data]);
        notify.success('Файл прикреплён');
      } catch (err: unknown) {
        notify.error(
          err instanceof Error ? err.message : 'Ошибка загрузки файла',
        );
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [baseUrl],
  );

  const handleFilesSelected = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) {return;}
      // Загружаем последовательно, чтобы не перегружать сервер.
      // Итерируем через индекс — FileList iterable, но eslint-unicorn
      // ворчит и на spread, и на Array.from().
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (!file) {continue;}
        await handleUpload(file);
      }
    },
    [handleUpload],
  );

  const handleDelete = useCallback(
    async (attachmentId: number) => {
      setDeletingId(attachmentId);
      try {
        const res = await fetch(`${baseUrl}/${attachmentId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
        notify.success('Вложение удалено');
      } catch (err: unknown) {
        notify.error(err instanceof Error ? err.message : 'Ошибка удаления');
      } finally {
        setDeletingId(null);
      }
    },
    [baseUrl],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {return;}

      const oldIndex = attachments.findIndex((a) => a.id === active.id);
      const newIndex = attachments.findIndex((a) => a.id === over.id);
      if (oldIndex === -1 || newIndex === -1) {return;}

      // Оптимистично обновляем UI, сразу пересчитывая локальный order
      const reordered = arrayMove(attachments, oldIndex, newIndex).map(
        (item, index) => ({ ...item, order: index }),
      );
      setAttachments(reordered);

      try {
        const res = await fetch(`${baseUrl}/reorder`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            attachmentIds: reordered.map((a) => a.id),
          }),
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
      } catch (err: unknown) {
        notify.error(
          err instanceof Error ? err.message : 'Не удалось сохранить порядок',
        );
        // Откатываем UI на прежнее состояние
        void reload();
      }
    },
    [attachments, baseUrl, reload],
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Paperclip className="h-4 w-4" />
          Прикреплённые файлы
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Dropzone / Upload button */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            if (!uploading) {
              void handleFilesSelected(e.dataTransfer.files);
            }
          }}
          className={cn(
            'relative rounded-lg border-2 border-dashed p-6 text-center transition-colors',
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-primary/50',
            uploading && 'pointer-events-none opacity-60',
          )}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            onChange={(e) => {
              void handleFilesSelected(e.target.files);
              e.target.value = '';
            }}
            className="absolute inset-0 cursor-pointer opacity-0"
            disabled={uploading}
          />
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {isDragging
                ? 'Отпустите для загрузки'
                : 'Перетащите файлы или нажмите, чтобы выбрать'}
            </p>
            <p className="text-xs text-muted-foreground">
              Ученикам файлы доступны только после покупки курса
              (или бесплатно, если урок — превью). Максимум 5 ГБ на файл.
            </p>
          </div>
        </div>

        {uploading && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Загрузка…</span>
              <span>{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        )}

        {/* List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (attachments.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground">
            Пока нет прикреплённых файлов.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={attachments.map((a) => a.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-2">
                {attachments.map((attachment) => (
                  <SortableAttachment
                    key={attachment.id}
                    attachment={attachment}
                    onDelete={handleDelete}
                    isDeleting={deletingId === attachment.id}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ))}
      </CardContent>
    </Card>
  );
}
